// This file will manage the state of the mention editor.
import { useMemo, useRef, useState } from 'react'

import { Segment, User } from '../types'
import { parseStringToSegments } from '../utils/segmentUtils'

export interface UseEditorStateProps {
  users: User[]
  initialValue?: string
}

export const useEditorState = (props: UseEditorStateProps) => {
  const { users, initialValue } = props
  const nextId = useRef(0)

  const initialSegments: Segment[] = useMemo(() => {
    if (initialValue) {
      return parseStringToSegments(initialValue, nextId, users)
    }
    return [{ id: `seg-${nextId.current++}`, type: 'text', content: '' }]
  }, [initialValue, users])

  const [segments, setSegments] = useState<Segment[]>(initialSegments)
  const [activeIndex, setActiveIndex] = useState(
    initialSegments.length > 0 ? initialSegments.length - 1 : 0,
  )
  const [isNavigating, setIsNavigating] = useState(!initialValue)
  const [globalCursorPos, setGlobalCursorPos] = useState(0)
  const [isMentionPopupOpen, setIsMentionPopupOpen] = useState(false)
  const [mentionQuery, setMentionQuery] = useState('')
  const [filteredUsers, setFilteredUsers] = useState<User[]>(users)
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0)
  const [popupCoords, setPopupCoords] = useState({ top: 0, left: 0 })
  const [editingMentionState, setEditingMentionState] = useState<{
    segmentId: string
    originalContent: string
  } | null>(null)

  const editorRef = useRef<HTMLDivElement>(null)
  const elRefs = useRef(new Map<string, HTMLElement>())
  const nextCursorPos = useRef<number | 'start' | 'end' | null>(
    initialValue ? initialValue.length : 0,
  )
  const isProcessing = useRef(false)
  const shouldRefocusOnNavigate = useRef(true)
  const isSelectionProgrammatic = useRef(false)
  const focusRequestCounter = useRef(0)
  const selectionRequestRef = useRef<any>(null)

  const requestFocus = () => {
    focusRequestCounter.current++
  }

  const resetEditor = () => {
    const newId = `seg-${nextId.current++}`
    setSegments([{ id: newId, type: 'text', content: '' }])
    setActiveIndex(0)
    setIsNavigating(false)
    setIsMentionPopupOpen(false)
    setMentionQuery('')
    setFilteredUsers(users)
    nextCursorPos.current = 0
    requestFocus()
  }

  const blurEditor = () => {
    setIsNavigating(false)
    setActiveIndex(-1)
  }

  const updateSegmentStyle = (
    segmentId: string,
    style: { fontSize?: number; color?: string },
  ) => {
    setSegments(prevSegments =>
      prevSegments.map(segment =>
        segment.id === segmentId ? { ...segment, ...style } : segment,
      ),
    )
  }

  return {
    users,
    isNavigating,
    setIsNavigating,
    globalCursorPos,
    setGlobalCursorPos,
    segments,
    setSegments,
    activeIndex,
    setActiveIndex,
    isMentionPopupOpen,
    setIsMentionPopupOpen,
    mentionQuery,
    setMentionQuery,
    filteredUsers,
    setFilteredUsers,
    activeSuggestionIndex,
    setActiveSuggestionIndex,
    popupCoords,
    setPopupCoords,
    editorRef,
    elRefs,
    nextCursorPos,
    nextId,
    isProcessing,
    shouldRefocusOnNavigate,
    editingMentionState,
    setEditingMentionState,
    isSelectionProgrammatic,
    selectionRequestRef,
    focusRequestCounter,
    requestFocus,
    resetEditor,
    blurEditor,
    updateSegmentStyle,
  }
}

export type UseEditorStateReturn = ReturnType<typeof useEditorState> 